import { TABLE_COST_LIMIT_MESSAGE } from '../analysis/constants'
import { useTableSources } from '../hooks/useTableSources'
import { sourceColumns, sourceStatusLabels, defaultSourceQuery } from './columns'
import { TableSourceQuery } from './schemas'
import { tableStyles } from '../components/table-styles'
import TableFileCell from '../components/TableFileCell'
import { IconButton } from '@/components/icon-button/IconButton'
import { Button } from '@/components/button/Button'
import { ChevronLeft, ChevronRight, TrashDelete, XClose } from '@/assets/icons'
import DeleteTableItemDialog from '../components/DeleteTableItemDialog'
import Checkbox from '@/components/checkbox/Checkbox'
import { useEffect, useState } from 'react'

const noHiddenColumns: string[] = []

export default function SourceFilesTab({
  tableId,
  matterId,
  canEdit = false,
  costLimitReached = false,
  onRemoved,
  query = defaultSourceQuery,
  onQueryChange,
  hiddenColumns = noHiddenColumns,
  fillAvailableHeight = false,
}: {
  tableId: string
  matterId: string
  canEdit?: boolean
  costLimitReached?: boolean
  onRemoved?: () => void
  query?: TableSourceQuery
  onQueryChange?: (query: TableSourceQuery) => void
  hiddenColumns?: string[]
  fillAvailableHeight?: boolean
}) {
  const { page, displayedQuery, stale, loading, error, reload, offset, setOffset, removal, removing, removalError, remove, requestRemoval, cancelRemoval } =
    useTableSources({ tableId, query, onQueryChange, onRemoved, hiddenColumns })
  const sources = page?.items
  const processing = Boolean(page?.processing)
  const limit = query.limit
  const selectionScope = JSON.stringify([tableId, query, offset])
  const [selection, setSelection] = useState<{ scope: string; ids: string[] }>({ scope: selectionScope, ids: [] })
  useEffect(() => {
    setSelection({ scope: selectionScope, ids: [] })
  }, [selectionScope])
  const selectedSources = selection.scope === selectionScope ? sources?.filter((source) => selection.ids.includes(source.id)) ?? [] : []
  const removalDisabled = loading || stale || removing || processing
  const removalRows = removal?.reduce((total, source) => total + source.rows_created, 0) ?? 0
  return (
    <div className={`${tableStyles.panel} min-w-0 ${fillAvailableHeight ? 'min-h-0 flex-1' : ''}`} aria-busy={loading}>
      {removal && removal.length > 0 && (
        <DeleteTableItemDialog
          title={`Remove ${removal.length === 1 ? removal[0].document_name : `${removal.length} files`}?`}
          subtitle={`This removes ${removalRows} ${removalRows === 1 ? 'row' : 'rows'} and any edits to them from this table. ${
            removal.length === 1 ? 'The original file stays' : 'The original files stay'
          } in Matter Files. This cannot be undone.`}
          confirmLabel={removal.length === 1 ? 'Remove file' : 'Remove files'}
          saving={removing}
          error={removalError}
          onCancel={cancelRemoval}
          onConfirm={() => void remove()}
        />
      )}
      {canEdit && selectedSources.length > 0 && (
        <div role="toolbar" aria-label="Selected source files" className="flex shrink-0 items-center gap-2 text-sm">
          <IconButton
            icon={<XClose />}
            size="sm"
            variant="plain"
            color="neutral"
            aria-label="Clear source file selection"
            disabled={removing}
            onClick={() => setSelection({ scope: selectionScope, ids: [] })}
          />
          <span role="status">
            {selectedSources.length} {selectedSources.length === 1 ? 'item' : 'items'} selected
          </span>
          <Button
            size="sm"
            variant="plain"
            color="destructive"
            leadingIcon={<TrashDelete />}
            disabled={removalDisabled || selectedSources.some((source) => Boolean(source.removal_blocked_reason))}
            onClick={() => requestRemoval(selectedSources)}
          >
            Delete
          </Button>
        </div>
      )}
      {error && (
        <div role="alert" className="p-3 text-sm text-red-800">
          {error}{' '}
          <Button size="sm" variant="plain" onClick={reload}>
            Reload source files
          </Button>
        </div>
      )}
      {!sources && !error && (
        <p role="status" className="p-3">
          Loading source files…
        </p>
      )}
      {sources && (
        <div className={`flex flex-col ${tableStyles.container} ${fillAvailableHeight ? tableStyles.filledContainer : ''}`}>
          {sources.length === 0 && (
            <p className="shrink-0 p-3 text-sm text-brand-neutral-600">
              {displayedQuery.search?.trim()
                ? 'No files match this search.'
                : displayedQuery.filters.length
                ? 'No files match these filters.'
                : 'No source files yet. Add documents to populate this table.'}
            </p>
          )}
          {sources.length > 0 && (
            <table aria-label="Source files" className={`${tableStyles.table} w-full shrink-0 text-left`}>
              <thead className={tableStyles.head}>
                <tr>
                  {canEdit && (
                    <th scope="col" className={tableStyles.headerCell} style={{ width: 32 }}>
                      <span className="sr-only">Row</span>
                    </th>
                  )}
                  {sourceColumns
                    .filter((column) => !hiddenColumns.includes(column.id))
                    .map((column) => {
                      const sort = displayedQuery.sorts.find((sort) => sort.column_id === column.id)
                      return (
                        <th
                          key={column.id}
                          scope="col"
                          aria-sort={sort ? (sort.descending ? 'descending' : 'ascending') : 'none'}
                          className={tableStyles.headerCell}
                        >
                          <button
                            type="button"
                            aria-label={`Sort ${column.name}`}
                            className="block min-h-11 w-full px-4 py-2 text-left after:absolute after:inset-0"
                            onClick={() =>
                              onQueryChange?.({ ...query, offset: 0, sorts: sort?.descending ? [] : [{ column_id: column.id, descending: Boolean(sort) }] })
                            }
                          >
                            {column.name}
                            {sort ? (sort.descending ? ' ↓' : ' ↑') : ''}
                          </button>
                        </th>
                      )
                    })}
                </tr>
              </thead>
              <tbody>
                {sources.map((source, index) => (
                  <tr key={source.id} className="align-top">
                    {canEdit && (
                      <td className={`group/selection ${tableStyles.selectionCell}`}>
                        <label
                          className="relative mx-auto flex h-8 w-8 cursor-pointer items-center justify-center"
                          title={source.removal_blocked_reason ?? (processing ? 'Wait for document processing to finish before removing files.' : undefined)}
                        >
                          <span
                            aria-hidden
                            className={
                              selectedSources.includes(source)
                                ? 'invisible'
                                : 'text-xs tabular-nums text-brand-neutral-600 group-hover/selection:invisible group-focus-within/selection:invisible'
                            }
                          >
                            {displayedQuery.offset + index + 1}
                          </span>
                          <span
                            className={
                              'absolute inset-0 flex items-center justify-center ' +
                              (selectedSources.includes(source) ? '' : 'opacity-0 group-hover/selection:opacity-100 group-focus-within/selection:opacity-100')
                            }
                          >
                            <Checkbox
                              checked={selectedSources.includes(source)}
                              disabled={removalDisabled || Boolean(source.removal_blocked_reason)}
                              onCheckedChange={(checked) =>
                                setSelection({
                                  scope: selectionScope,
                                  ids: checked
                                    ? [...selectedSources.map((item) => item.id), source.id]
                                    : selectedSources.filter((item) => item.id !== source.id).map((item) => item.id),
                                })
                              }
                            />
                          </span>
                          <span className="sr-only">Select {source.document_name}</span>
                        </label>
                      </td>
                    )}
                    {!hiddenColumns.includes('document_name') && (
                      <td className={`${tableStyles.cellBorder} ${tableStyles.cellBackground} ${tableStyles.cellPadding}`}>
                        <TableFileCell matterId={matterId} document={{ id: source.document_id, name: source.document_name }} />
                      </td>
                    )}
                    {!hiddenColumns.includes('status') && (
                      <td className={`${tableStyles.cellBorder} ${tableStyles.cellBackground} ${tableStyles.cellPadding}`}>
                        {costLimitReached && (source.status === 'ready' || source.status === 'cancelled')
                          ? 'Analysis paused'
                          : sourceStatusLabels[source.status]}
                        {source.error && !(source.status === 'cancelled' && source.error === TABLE_COST_LIMIT_MESSAGE) && (
                          <p className="mt-1 text-red-800">{source.error}</p>
                        )}
                      </td>
                    )}
                    {!hiddenColumns.includes('rows_created') && (
                      <td className={`${tableStyles.cellBorder} ${tableStyles.cellBackground} ${tableStyles.cellPadding}`}>{source.rows_created}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {page && (
            <>
              {displayedQuery.search?.trim() && (
                <div className="flex shrink-0 items-center gap-2 text-sm text-brand-neutral-600">
                  <span role="status">{loading ? 'Searching…' : `${page.matched.toLocaleString()} matching files`}</span>
                  <Button size="sm" variant="plain" onClick={() => onQueryChange?.({ ...query, search: '', offset: 0 })}>
                    Clear search
                  </Button>
                </div>
              )}
              {(displayedQuery.offset > 0 || page.matched > displayedQuery.limit) && (
                <nav aria-label="Source files pagination" className={`${tableStyles.pagination} sticky left-0 mt-auto py-4`}>
                  <IconButton
                    icon={<ChevronLeft />}
                    aria-label="Previous"
                    variant="tonal"
                    size="xs"
                    disabled={loading || offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                  />
                  <span className="text-xs text-brand-neutral-950">
                    Page {Math.floor(displayedQuery.offset / displayedQuery.limit) + 1} of {Math.max(1, Math.ceil(page.matched / displayedQuery.limit))}
                  </span>
                  <IconButton
                    icon={<ChevronRight />}
                    aria-label="Next"
                    variant="tonal"
                    size="xs"
                    disabled={loading || offset + limit >= page.matched}
                    onClick={() => setOffset(offset + limit)}
                  />
                </nav>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
